/* global axios */
import ApiClient from './ApiClient';
import { getCurrentLocale } from 'dashboard/helper/locale';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getDrilldown({
    metric,
    bucketTimestamp,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    page,
    perPage,
    signal,
  }) {
    const requestConfig = {
      params: {
        metric,
        bucket_timestamp: bucketTimestamp,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        page,
        per_page: perPage,
      },
    };

    if (signal) {
      requestConfig.signal = signal;
    }

    return axios.get(`${this.url}/drilldown`, requestConfig);
  }

  // eslint-disable-next-line default-param-last
  getSummary(since, until, type = 'account', id, groupBy, businessHours) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/agents`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        locale: getCurrentLocale(),
      },
    });
  }

  getConversationsSummaryReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        locale: getCurrentLocale(),
      },
    });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: {
        timezone_offset: getTimeOffset(),
        days_before: daysBefore,
        locale: getCurrentLocale(),
      },
    });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        locale: getCurrentLocale(),
      },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        locale: getCurrentLocale(),
      },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: {
        since,
        until,
        business_hours: businessHours,
        locale: getCurrentLocale(),
      },
    });
  }

  getClosingSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/closing_summary`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  downloadClosingSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/closing_summary_export`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  getAgentLoginTimes({ from: since, to: until } = {}) {
    return axios.get(`${this.url}/agent_login_times`, {
      params: {
        since,
        until,
        timezone_offset: getTimeOffset(),
        locale: getCurrentLocale(),
      },
    });
  }

  downloadAgentLoginTimes({ from: since, to: until } = {}) {
    return axios.get(`${this.url}/agent_login_times_export`, {
      params: {
        since,
        until,
        timezone_offset: getTimeOffset(),
        locale: getCurrentLocale(),
      },
    });
  }

  getTransferSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/transfer_summary`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  downloadTransferSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/transfer_summary_export`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  getRescheduleSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/reschedule_summary`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  downloadRescheduleSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/reschedule_summary_export`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  getContactReasonSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/contact_reason_summary`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  downloadContactReasonSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/contact_reason_summary_export`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  getHourlySummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/hourly_summary`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        timezone_offset: getTimeOffset(),
        locale: getCurrentLocale(),
      },
    });
  }

  downloadHourlySummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/hourly_summary_export`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        timezone_offset: getTimeOffset(),
        locale: getCurrentLocale(),
      },
    });
  }

  getReopenSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/reopen_summary`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  downloadReopenSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/reopen_summary_export`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  getNoResponseSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/no_response_summary`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  downloadNoResponseSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/no_response_summary_export`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  getWhatsappTemplateSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/whatsapp_template_summary`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  downloadWhatsappTemplateSummary({ from: since, to: until, inboxId } = {}) {
    return axios.get(`${this.url}/whatsapp_template_summary_export`, {
      params: {
        since,
        until,
        inbox_id: inboxId,
        locale: getCurrentLocale(),
      },
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      },
    });
  }
}

export default new ReportsAPI();
